import React, { useMemo, useCallback } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import sanitizeSvg from "../visuals/sanitizeSvg";

/**
 * Model-authored diagram, rendered as inline SVG — no iframe.
 *
 * SVG stripped of scripts, foreignObject, external references and event
 * handlers is inert, so it can live in the DOM directly. That buys correct
 * text rendering, real font inheritance, and no resize plumbing, at the cost
 * of a sanitizer we own. See sanitizeSvg.js — it is the security boundary.
 *
 * Streaming: block_delta appends to `svg`, but partial SVG is not parseable,
 * so nothing is sanitized or mounted until isStreaming goes false. A skeleton
 * holds the space instead of flashing half-drawn markup.
 *
 * Interaction: a <g data-prompt="..."> is a menu item into a deeper answer.
 * The click is handled by delegation on the container — the injected subtree
 * is not React-managed, so per-node handlers are not an option. The attribute
 * value has already been stripped of markup characters by the sanitizer.
 */
const DiagramBlock = observer(({ block, isStreaming }) => {
  const result = useMemo(() => {
    if (isStreaming || !block.svg) return null;
    return sanitizeSvg(block.svg);
  }, [isStreaming, block.svg]);

  const handleClick = useCallback(
    event => {
      if (copilotStore.streaming) return;
      const target = event.target.closest
        ? event.target.closest("[data-prompt]")
        : null;
      if (!target) return;
      const prompt = target.getAttribute("data-prompt");
      if (prompt) copilotStore.sendUserMessage(prompt);
    },
    []
  );

  if (isStreaming || !block.svg) {
    return (
      <div className="cpVisual cpDiagram cpDiagram--pending">
        {block.title && <div className="cpVisual__title">{block.title}</div>}
        <div className="cpDiagram__skeleton" aria-label="Drawing diagram" />
      </div>
    );
  }

  // Rejected rather than truncated: say so plainly instead of showing a
  // half-diagram the user might read as real.
  if (!result || !result.ok) {
    return (
      <div className="cpVisual cpDiagram cpDiagram--failed">
        {block.title && <div className="cpVisual__title">{block.title}</div>}
        <div className="cpDiagram__error">
          This diagram couldn&apos;t be displayed safely.
          {result && result.reason ? ` (${result.reason})` : ""}
        </div>
      </div>
    );
  }

  return (
    <div className="cpVisual cpDiagram">
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="cpDiagram__canvas"
        // Never scale a diagram beyond the size it was authored at — see
        // sanitizeSvg. Smaller containers still shrink it responsively.
        style={
          result.intrinsicWidth
            ? { maxWidth: `${Math.round(result.intrinsicWidth)}px` }
            : undefined
        }
        role="img"
        aria-label={block.title || "Diagram"}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: result.svg }}
      />
    </div>
  );
});

export default DiagramBlock;
